import Card from "@/components/Card";
import FormHeader from "@/components/FormHeader";
import ImageControl from "@/components/ImageControll";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import SkeletonCard from "@/components/SkeletonCard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/axios";
import { DataBerkas, DataKeluarga, DataSiswa } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  FileText,
  Info,
  Loader2,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { GiBackwardTime } from "react-icons/gi";
import { MdBlockFlipped } from "react-icons/md";

// --- API Calls ---
const patchDataValidate = async ({
  id_data_berkas_siswa,
  is_valid,
  catatan_verifikator,
}: {
  id_data_berkas_siswa: string;
  is_valid: boolean;
  catatan_verifikator?: string;
}) => {
  const response = await api.patch(
    `/verifikator/peserta/data-berkas/validate`,
    { id_data_berkas_siswa, is_valid, catatan_verifikator },
  );
  return response.data;
};

const patchDataReset = async ({
  id_data_berkas_siswa,
}: {
  id_data_berkas_siswa: string;
}) => {
  const response = await api.patch(`/verifikator/peserta/data-berkas/reset`, {
    id_data_berkas_siswa,
  });
  return response.data;
};

// --- Helper Component: DataRow ---
const DataRow = ({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}) => (
  <div className="flex flex-col sm:flex-row sm:items-start py-3 border-b border-slate-100 dark:border-slate-800/60 last:border-0">
    <span className="text-sm font-medium text-slate-500 dark:text-slate-400 sm:w-1/3 shrink-0">
      {label}
    </span>
    <span
      className={`text-sm mt-1 sm:mt-0 ${highlight ? "font-bold text-primary" : "font-medium text-slate-900 dark:text-slate-100"}`}
    >
      {value || "-"}
    </span>
  </div>
);

export default function Berkas({
  dataBerkas,
  dataSiswa,
  dataKeluarga,
  isPending,
}: {
  dataBerkas: DataBerkas[] | undefined;
  dataSiswa: DataSiswa | undefined;
  dataKeluarga?: DataKeluarga[] | undefined;
  isPending: boolean;
}) {
  const [showInput, setShowInput] = useState(false);
  const [selectedBerkas, setSelectedBerkas] = useState<DataBerkas | null>(null);
  const [form, setForm] = useState({
    catatan_verifikator: "",
  });

  const queryClient = useQueryClient();

  const {
    mutate,
    isPending: isPendingValidate,
    error,
  } = useMutation({
    mutationFn: patchDataValidate,
    onSuccess: (data) => {
      setForm((prev) => ({ ...prev, catatan_verifikator: "" }));
      setShowInput(false);
      queryClient.invalidateQueries({ queryKey: ["peserta"] });
      toast.success(data?.message || "Berhasil memvalidasi berkas.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const { mutate: mutateResetValidation, isPending: isPendingResetValidation } =
    useMutation({
      mutationFn: patchDataReset,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["peserta"] });
        toast.success(data?.message || "Reset validasi berhasil.");
      },
      onError: (error: AxiosError<{ message?: string }>) => {
        toast.error(
          error?.response?.data?.message || "An unexpected error occurred.",
        );
      },
    });

  const handleValidate = (
    id_data_berkas_siswa: string,
    is_valid: boolean,
    catatan_verifikator?: string,
  ) => {
    mutate({ id_data_berkas_siswa, is_valid, catatan_verifikator });
  };

  const handleReset = (id_data_berkas_siswa: string) => {
    mutateResetValidation({ id_data_berkas_siswa });
  };

  if (isPending)
    return (
      <div className="flex flex-col gap-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );

  return (
    <div className="flex flex-col gap-8 w-full p-2 sm:p-4">
      {/* INFO ALERT */}
      <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900/50">
        <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-500" />
        <AlertTitle className="text-blue-800 dark:text-blue-400 font-bold ml-2">
          Pemeriksaan Berkas Pendukung
        </AlertTitle>
        <AlertDescription className="text-blue-700/80 dark:text-blue-500/80 ml-2 mt-1">
          Pastikan dokumen yang diunggah dapat dibaca dengan jelas, tidak
          terpotong, dan sesuai dengan ketentuan (KIP, Foto Rumah, dll).
        </AlertDescription>
      </Alert>

      {/* --- LOOP DATA BERKAS --- */}
      {dataBerkas && dataBerkas.length > 0 ? (
        dataBerkas.map((item, index) => {
          const url = item.path_berkas;
          const extension = url?.split(".").pop()?.toLowerCase();
          const isValid = item.is_valid;
          const isPendingItemAction =
            isPendingResetValidation || isPendingValidate;

          return (
            <Card
              key={index}
              className="overflow-hidden border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl"
            >
              <div className="grid xl:grid-cols-2 grid-cols-1 divide-y xl:divide-y-0 xl:divide-x divide-slate-100 dark:divide-slate-800">
                {/* --- KIRI: AREA DOKUMEN VIEWER --- */}
                <div className="flex flex-col bg-slate-50/50 dark:bg-slate-900/20">
                  <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 text-primary rounded-lg">
                        <FileText size={20} />
                      </div>
                      <div>
                        <h2 className="font-bold text-slate-800 dark:text-slate-200 text-base">
                          Pratinjau Dokumen
                        </h2>
                      </div>
                    </div>
                  </div>
                  <div className="relative h-[60vh] xl:h-[70vh] w-full bg-slate-200/50 dark:bg-slate-950 flex items-center justify-center p-2">
                    {extension === "pdf" ? (
                      <iframe
                        src={item?.path_berkas}
                        title="Berkas"
                        className="absolute inset-0 h-full w-full rounded-b-xl xl:rounded-bl-xl xl:rounded-br-none shadow-inner bg-white"
                      />
                    ) : item.path_berkas ? (
                      <ImageControl imageUrl={item.path_berkas} />
                    ) : (
                      <div className="text-slate-400 flex flex-col items-center">
                        <FileText size={40} className="mb-2 opacity-50" />
                        <p className="text-sm font-medium">
                          Dokumen tidak tersedia
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* --- KANAN: DATA DETAIL & AKSI --- */}
                <div className="flex flex-col bg-white dark:bg-slate-900 h-full">
                  {/* Header Detail */}
                  <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/20">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 text-primary font-bold text-sm border-2 border-white dark:border-slate-800 shadow-sm shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <h2 className="font-bold text-lg text-slate-800 dark:text-slate-100 uppercase">
                          {item.sys_berkas?.jenis_berkas?.nama_jenis_berkas}
                        </h2>
                        {isValid === true && (
                          <Badge variant="success" className="mt-1 shadow-none">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Valid
                          </Badge>
                        )}
                        {isValid === false && (
                          <Badge
                            variant="destructive"
                            className="mt-1 shadow-none"
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Tidak Valid
                          </Badge>
                        )}
                        {isValid === null && (
                          <Badge
                            variant="secondary"
                            className="mt-1 shadow-none bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                          >
                            Belum Diperiksa
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Body Kanan (Scrollable Data) */}
                  <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
                    <div className="space-y-1 mb-6">
                      <DataRow
                        label="Deskripsi Berkas"
                        value={
                          item?.sys_berkas?.keterangan_berkas ||
                          "Tidak ada keterangan khusus."
                        }
                      />
                      {item.nomor && (
                        <DataRow
                          label="Nomor Dokumen"
                          value={
                            <span className="font-mono text-base">
                              {item.nomor}
                            </span>
                          }
                          highlight
                        />
                      )}
                    </div>

                    {/* Jika Berkas adalah KIP (Kartu Indonesia Pintar) */}
                    {item?.sys_berkas?.jenis_berkas?.slug_jenis_berkas ===
                      "KIP" && (
                      <div className="mb-6 p-4 border border-blue-200 bg-blue-50/50 dark:border-blue-900/50 dark:bg-blue-950/30 rounded-xl space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-blue-900 dark:text-blue-300">
                            Cek Status KIP PIP
                          </h3>
                          <a
                            href="https://pip.kemendikdasmen.go.id/home_v1"
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-full transition-colors"
                          >
                            Buka Web PIP <ExternalLink size={14} />
                          </a>
                        </div>
                        <div className="grid grid-cols-2 gap-4 bg-white dark:bg-slate-900 p-3 rounded-lg border border-blue-100 dark:border-blue-900/40">
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              NISN Peserta
                            </p>
                            <p className="font-mono font-bold text-slate-800 dark:text-slate-200">
                              {dataSiswa?.nisn || "-"}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              NIK Peserta
                            </p>
                            <p className="font-mono font-bold text-slate-800 dark:text-slate-200">
                              {dataSiswa?.nik || "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {item?.sys_berkas?.jenis_berkas?.slug_jenis_berkas ===
                      "PKH" && (
                      <div className="mb-6 p-4 sm:p-5 border border-blue-200 bg-blue-50/60 dark:border-blue-900/50 dark:bg-blue-950/30 rounded-xl space-y-5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-blue-100 dark:bg-blue-900 rounded-lg text-blue-600 dark:text-blue-400">
                              <CheckCircle2 size={20} />
                            </div>
                            <h3 className="font-bold text-lg text-blue-900 dark:text-blue-300">
                              Verifikasi Status PKH
                            </h3>
                          </div>
                          <a
                            href="https://cekbansos.kemensos.go.id"
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full transition-colors shadow-sm w-full sm:w-auto"
                          >
                            Buka Portal Cek Bansos <ExternalLink size={14} />
                          </a>
                        </div>

                        {/* --- TAMBAHAN: INFO / PETUNJUK PENGECEKAN --- */}
                        <div className="flex items-start gap-3 p-3.5 bg-blue-100/50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 rounded-lg border border-blue-200/70 dark:border-blue-800/50">
                          <Info className="w-5 h-5 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
                          <div className="text-xs sm:text-sm leading-relaxed">
                            <span className="font-bold block mb-1">
                              Petunjuk Verifikasi:
                            </span>
                            Gunakan NIK dari nama penerima bantuan yang terdapat
                            pada berkas. Jika hasil pencarian menunjukkan status
                            penerima PKH bernilai{" "}
                            <strong className="bg-blue-200/50 dark:bg-blue-800/50 px-1.5 py-0.5 rounded text-blue-900 dark:text-blue-100">
                              "YA"
                            </strong>{" "}
                            , maka pendaftar dinyatakan{" "}
                            <strong>Valid terdata di PKH</strong>.
                          </div>
                        </div>

                        {/* Data Peserta Utama */}
                        <div className="space-y-3">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 px-1">
                            Identitas Peserta
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white dark:bg-slate-900 p-4 rounded-lg border border-blue-100 dark:border-blue-900/40 shadow-sm">
                            <div>
                              <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">
                                NIK Peserta
                              </p>
                              <p className="font-mono text-base font-bold text-slate-800 dark:text-slate-200">
                                {dataSiswa?.nik || "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">
                                Nama Lengkap
                              </p>
                              <p className="font-semibold text-slate-800 dark:text-slate-200 capitalize">
                                {dataSiswa?.nama_lengkap || "-"}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Data Keluarga */}
                        {dataKeluarga && dataKeluarga.length > 0 && (
                          <div className="space-y-3 pt-2">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 px-1">
                              Identitas Keluarga Terkait
                            </h4>
                            <div className="grid grid-cols-1 gap-3">
                              {dataKeluarga.map(
                                (keluarga: DataKeluarga, index) => (
                                  <div
                                    key={keluarga?.id_data_keluarga || index}
                                    className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-white dark:bg-slate-900 p-4 rounded-lg border border-blue-50 dark:border-blue-900/30 shadow-sm hover:border-blue-200 dark:hover:border-blue-700 transition-colors"
                                  >
                                    <div>
                                      <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">
                                        NIK{" "}
                                        {keluarga?.hubungan_keluarga
                                          ?.hubungan_keluarga || "Keluarga"}
                                      </p>
                                      <p className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                                        {keluarga?.nik || "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">
                                        Nama
                                      </p>
                                      <p className="font-semibold text-sm text-slate-800 dark:text-slate-200 capitalize">
                                        {keluarga?.nama || "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">
                                        No. Handphone
                                      </p>
                                      <p className="font-mono text-sm font-medium text-slate-700 dark:text-slate-300">
                                        {keluarga?.handphone || "-"}
                                      </p>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {item?.sys_berkas?.jenis_berkas?.slug_jenis_berkas ===
                      "DTKS" && (
                      <div className="mb-6 p-4 sm:p-5 border border-blue-200 bg-blue-50/60 dark:border-blue-900/50 dark:bg-blue-950/30 rounded-xl space-y-5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <h3 className="font-bold text-lg text-blue-900 dark:text-blue-300">
                            Cek Status Bansos / DTKS
                          </h3>
                          <a
                            href="https://cekbansos.kemensos.go.id"
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full transition-colors shadow-sm w-full sm:w-auto"
                          >
                            Buka Web Cek Bansos <ExternalLink size={14} />
                          </a>
                        </div>

                        {/* --- TAMBAHAN: INFO / PETUNJUK PENGECEKAN --- */}
                        <div className="flex items-start gap-3 p-3.5 bg-blue-100/50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 rounded-lg border border-blue-200/70 dark:border-blue-800/50">
                          <Info className="w-5 h-5 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
                          <div className="text-xs sm:text-sm leading-relaxed">
                            <span className="font-bold block mb-1">
                              Petunjuk Verifikasi:
                            </span>
                            Gunakan NIK dari nama penerima bantuan yang terdapat
                            pada berkas. Jika hasil pencarian menunjukkan status
                            penerima manfaat bernilai{" "}
                            <strong className="bg-blue-200/50 dark:bg-blue-800/50 px-1.5 py-0.5 rounded text-blue-900 dark:text-blue-100">
                              "YA"
                            </strong>{" "}
                            pada salah satu NIK yang bersangkutan (baik SEMBAKO,
                            PKH, atau PBI-JK), maka pendaftar dinyatakan{" "}
                            <strong>Valid terdata di DTKS</strong>.
                          </div>
                        </div>

                        {/* Menampilkan Data Siswa (Peserta) Utama */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-400">
                            Data Peserta Utama
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white dark:bg-slate-900 p-3.5 rounded-lg border border-blue-100 dark:border-blue-900/40 shadow-sm">
                            <div>
                              <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-0.5">
                                NIK Peserta
                              </p>
                              <p className="font-mono text-base font-bold text-slate-800 dark:text-slate-200">
                                {dataSiswa?.nik || "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-0.5">
                                Nama Peserta
                              </p>
                              <p className="font-semibold text-slate-800 dark:text-slate-200 capitalize">
                                {dataSiswa?.nama_lengkap || "-"}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Mapping Data Keluarga untuk keperluan pencocokan PKH (Ayah/Ibu) */}
                        {dataKeluarga && dataKeluarga.length > 0 && (
                          <div className="space-y-3 pt-2">
                            <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-400">
                              Data Anggota Keluarga
                            </h4>
                            <div className="grid grid-cols-1 gap-3">
                              {dataKeluarga.map(
                                (keluarga: DataKeluarga, index) => (
                                  <div
                                    key={keluarga?.id_data_keluarga || index}
                                    className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-white dark:bg-slate-900 p-3.5 rounded-lg border border-blue-100 dark:border-blue-900/40 shadow-sm"
                                  >
                                    <div>
                                      <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-0.5">
                                        NIK{" "}
                                        {keluarga?.hubungan_keluarga
                                          ?.hubungan_keluarga || "Keluarga"}
                                      </p>
                                      <p className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
                                        {keluarga?.nik || "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-0.5">
                                        Nama
                                      </p>
                                      <p className="font-semibold text-sm text-slate-800 dark:text-slate-200 capitalize">
                                        {keluarga?.nama || "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-0.5">
                                        No. Handphone
                                      </p>
                                      <p className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-200">
                                        {keluarga?.handphone || "-"}
                                      </p>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Jika Tidak Valid dan ada Catatan */}
                    {item.is_valid === false && item.catatan_verifikator && (
                      <div className="mt-4 p-4 bg-red-50 dark:bg-red-950/30 rounded-xl border border-red-100 dark:border-red-900/50">
                        <p className="text-xs font-semibold text-red-800 dark:text-red-400 mb-1">
                          Alasan Penolakan Berkas:
                        </p>
                        <p className="text-sm text-red-700 dark:text-red-300 italic">
                          "{item.catatan_verifikator}"
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Footer Kanan (Action Buttons) */}
                  <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex flex-col sm:flex-row justify-end gap-3 mt-auto">
                    {isValid !== null ? (
                      <Button
                        variant="outline"
                        onClick={() => handleReset(item.id_data_berkas_siswa)}
                        disabled={isPendingItemAction}
                        className="w-full sm:w-auto dark:border-slate-700"
                      >
                        {isPendingResetValidation ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <GiBackwardTime className="mr-2 h-4 w-4" />
                        )}
                        Reset Hasil Validasi
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="destructive"
                          disabled={isPendingItemAction}
                          className="w-full sm:w-auto"
                          onClick={() => {
                            setShowInput(true);
                            setSelectedBerkas(item);
                          }}
                        >
                          <XCircle className="w-4 h-4 mr-2" /> Tidak Valid
                        </Button>
                        <Button
                          className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white"
                          disabled={isPendingItemAction}
                          onClick={() =>
                            handleValidate(item.id_data_berkas_siswa, true)
                          }
                        >
                          {isPendingValidate ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                          )}
                          Valid (Sesuai Dokumen)
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })
      ) : (
        <div className="p-10 text-center bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
          <FileText className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <p className="text-slate-500 font-medium">
            Data berkas pendukung tidak ditemukan.
          </p>
        </div>
      )}

      {/* ================= MODAL PENOLAKAN BERKAS ================= */}
      <Modal show={showInput} onClose={() => setShowInput(false)}>
        <form
          className="flex flex-col gap-y-5 p-1"
          onSubmit={(event) => {
            event.preventDefault();
            handleValidate(
              selectedBerkas?.id_data_berkas_siswa || "",
              false,
              form.catatan_verifikator,
            );
          }}
        >
          <FormHeader
            title="Tolak Berkas Pendukung"
            description={`Berikan alasan kenapa dokumen ${selectedBerkas?.sys_berkas?.jenis_berkas?.nama_jenis_berkas || ""} tidak valid.`}
            icon={<MdBlockFlipped className="text-red-500 h-6 w-6" />}
          />
          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">
              Alasan Penolakan <span className="text-red-500">*</span>
            </Label>
            <Textarea
              required
              placeholder="Contoh: Dokumen buram dan tidak terbaca..."
              value={form.catatan_verifikator}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  catatan_verifikator: event.target.value,
                }))
              }
              rows={4}
              className="bg-slate-50 dark:bg-slate-950 focus-visible:ring-red-500"
            />
            <InputError
              text={error?.response?.data?.errors?.catatan_verifikator?.join(
                ",",
              )}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowInput(false)}
              disabled={isPendingValidate}
              className="dark:border-slate-700"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={isPendingValidate}
              variant="destructive"
            >
              {isPendingValidate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                "Simpan Penolakan"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
